/**
 * BulletNodeView — React NodeView for the editable `bullet` node.
 *
 * Phase 4 restores the visual speaker prefix we lost in Phase 3
 * (when speaker became a structural attr instead of inline text).
 * The chip renders as a non-editable inline `<strong>` ahead of
 * the body via NodeViewContent, so:
 *   - The user sees "Alice: hello world" exactly like the legacy
 *     preview did.
 *   - The editable surface contains only the body. Edits to the
 *     bullet body don't accidentally clobber the speaker name.
 *
 * Wave-2 audit fix: the ambiguity flag (terra-cotta `[unclear?]`
 * chip) used to render via a CSS ::after rule on the wrapper —
 * decorative-only, not clickable. A pilot user spotting a flag
 * had no inline path to act on it; they had to open the
 * AmbiguityResolverDialog from AiEnhancements. Now the chip is a
 * real React button that opens a popover with Dismiss + Edit
 * actions, and the CSS rule has been removed from
 * TiptapStructuredEditor.
 */
import { useEffect, useRef, useState } from 'react';
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { X } from 'lucide-react';
import { ReorderControls } from './ReorderControls.tsx';
import { usePopover } from '../usePopover.ts';

export function BulletNodeView({ node, editor, getPos, updateAttributes }: NodeViewProps) {
  const { isDirectQuote, ambiguityFlag, speaker } = node.attrs as {
    isDirectQuote?: boolean;
    ambiguityFlag?: string | null;
    speaker?: string | null;
  };

  const classes = ['tt-bullet'];
  if (isDirectQuote) classes.push('tt-bullet--quote');
  // tt-bullet--flag class no longer drives the chip rendering
  // (we render a real React button instead) — kept here for any
  // legacy CSS hooks that target the class.
  if (ambiguityFlag) classes.push('tt-bullet--flag');
  if (speaker) classes.push('tt-bullet--speaker');

  // The wrapper class list now includes 'group/bullet' so the
  // hover-only reorder controls below react to mouse-over via the
  // arbitrary group selector. The /row alias is included so the
  // shared ReorderControls hover rule (group-hover/row) also
  // matches. data-* attrs mirror what BulletNode.renderHTML emits
  // so existing CSS rules (e.g. data-direct-quote) still work.
  return (
    <NodeViewWrapper
      as="div"
      className={classes.concat(['group/bullet', 'group/row']).join(' ')}
      data-node="bullet"
      data-direct-quote={isDirectQuote ? 'true' : undefined}
      data-ambiguity={ambiguityFlag ?? undefined}
      data-speaker={speaker ?? undefined}
    >
      <ReorderControls
        editor={editor}
        getPos={getPos as () => number | undefined}
        label="bullet"
      />

      {/* Speaker prefix is intentionally NOT rendered. Per OJB pilot
          convention the speaker's name is baked into the body prose
          in third person ("Patrick Martin raised RFI 042…"), not
          surfaced as a separate "Speaker:" label. The `speaker` attr
          is preserved on the node so older docs round-trip cleanly,
          but it's invisible. */}
      {/* NodeViewContent is where ProseMirror puts the editable
          body text. draggable={false} on this child prevents the
          schema-level draggable=true on the wrapper from
          breaking text selection: drag is now initiated only
          from the wrapper's non-text padding/margin area. */}
      <NodeViewContent
        as="span"
        className="tt-bullet__body"
        draggable={false}
      />

      {/* Ambiguity flag chip — interactive on editable docs,
          read-only label otherwise. Renders inline at the end of
          the bullet body so the spatial association with the
          flagged text is preserved. */}
      {ambiguityFlag && (
        <AmbiguityChip
          flag={ambiguityFlag}
          isEditable={editor.isEditable}
          onDismiss={() => updateAttributes({ ambiguityFlag: null })}
          onEdit={(next) => updateAttributes({ ambiguityFlag: next || null })}
        />
      )}
    </NodeViewWrapper>
  );
}

/**
 * Inline chip rendered at the end of a flagged bullet. Shows the
 * AI's flag text (e.g. "speaker uncertain") in a terra-cotta pill;
 * clicking opens a popover with Dismiss + Edit actions.
 *
 * Read-only docs (finalized meeting, archive view) render the
 * chip as a plain non-interactive span — Same colors, same shape,
 * no popover.
 */
function AmbiguityChip({
  flag,
  isEditable,
  onDismiss,
  onEdit,
}: {
  flag: string;
  isEditable: boolean;
  onDismiss: () => void;
  onEdit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(flag);
  useEffect(() => setDraft(flag), [flag]);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const flagRef = useRef(flag);
  flagRef.current = flag;

  const popover = usePopover<HTMLSpanElement>({
    onCommit: () => {
      // Commit edits on outside-click / Escape, but ignore empty
      // strings — those go through onDismiss explicitly.
      if (draftRef.current && draftRef.current !== flagRef.current) {
        onEdit(draftRef.current);
      }
    },
  });

  const baseClasses =
    'ml-2 inline-flex select-none items-center gap-1 rounded-full border border-terra-cotta/25 bg-terra-cotta/10 px-2 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-[0.12em] text-charcoal';

  if (!isEditable) {
    return (
      <span contentEditable={false} className={baseClasses}>
        {flag}
      </span>
    );
  }

  return (
    <span
      ref={popover.wrapperRef}
      contentEditable={false}
      className="relative inline-block"
    >
      <button
        type="button"
        onClick={popover.toggle}
        title="Click to dismiss or edit this flag"
        aria-label={`Ambiguity flag: ${flag}. Click to act on it.`}
        className={baseClasses + ' transition-colors hover:bg-terra-cotta/20'}
      >
        {flag}
      </button>
      {popover.isOpen && (
        <span className="absolute right-0 top-full z-30 mt-1 block w-56 rounded-lg border border-charcoal/20 bg-white p-2 shadow-xl">
          <div className="mb-1 px-1 text-[9px] font-bold uppercase tracking-[0.14em] text-pine">
            Ambiguity flag
          </div>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                popover.close();
              }
            }}
            placeholder="Flag text"
            className="w-full rounded border border-charcoal/25 px-2 py-1 text-[12px] focus:border-pine focus:outline-none"
          />
          <div className="mt-1.5 flex justify-between gap-1 text-[11px]">
            <button
              type="button"
              onClick={() => {
                onDismiss();
                popover.close();
              }}
              className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-charcoal transition-colors hover:bg-charcoal/5 hover:text-pine"
              title="Remove this flag from the bullet"
            >
              <X size={11} strokeWidth={2.5} />
              Dismiss
            </button>
            <button
              type="button"
              onClick={() => popover.close()}
              className="rounded-full bg-pine px-2.5 py-0.5 text-cloud transition-colors hover:bg-pine/90"
            >
              Save
            </button>
          </div>
        </span>
      )}
    </span>
  );
}
